import {
  readSampleTaskProvenance,
  sampleTaskProvenanceFromVoiceSample,
} from '../../utils/sampleTaskProvenance';

export const CAPE_VR_CONTRACT = Object.freeze({
  protocolKey: 'cape-vr',
  protocolVersion: '1.0',
  digitalAdaptationVersion: '1.0',
  representationVersion: '1.1',
});

export const CAPE_VR_STANDARD_KEYS = Object.freeze([
  'overallSeverity',
  'roughness',
  'breathiness',
  'strain',
]);

const nonBlank = value => typeof value === 'string' && value.trim() !== '';
const finiteVas = value => Number.isFinite(value) && value >= 0 && value <= 100;

const selectedCategory = (options) => options.find(option => option.response?.isPresent)?.value || null;
const selectedComment = (options) => options.find(option => option.response?.isPresent)?.response?.attributeComment || '';

export function capeVrSampleTaskProvenance(selectedSample) {
  return sampleTaskProvenanceFromVoiceSample(selectedSample);
}

function categories(state) {
  return {
    pitch: {
      value: selectedCategory([
        { value: 'normal', response: state.normalPitch },
        { value: 'low', response: state.tooLowPitch },
        { value: 'high', response: state.tooHighPitch },
      ]),
      comment: selectedComment([
        { response: state.normalPitch },
        { response: state.tooLowPitch },
        { response: state.tooHighPitch },
      ]),
    },
    loudness: {
      value: selectedCategory([
        { value: 'normal', response: state.normalLoudness },
        { value: 'quiet', response: state.tooQuietLoudness },
        { value: 'loud', response: state.tooLoudLoudness },
      ]),
      comment: selectedComment([
        { response: state.normalLoudness },
        { response: state.tooQuietLoudness },
        { response: state.tooLoudLoudness },
      ]),
    },
    resonance: {
      value: selectedCategory([
        { value: 'normal', response: state.normalResonance },
        { value: 'front', response: state.frontResonance },
        { value: 'back', response: state.backResonance },
      ]),
      comment: selectedComment([
        { response: state.normalResonance },
        { response: state.frontResonance },
        { response: state.backResonance },
      ]),
    },
    nasality: {
      value: selectedCategory([
        { value: 'normal', response: state.normalNasality },
        { value: 'hyponasal', response: state.hyponasality },
        { value: 'hypernasal', response: state.hypernasality },
      ]),
      comment: selectedComment([
        { response: state.normalNasality },
        { response: state.hyponasality },
        { response: state.hypernasality },
      ]),
    },
  };
}

const OTHER_KEYS = Object.freeze({
  instabilities: 'otherInstability',
  additionalFeatures: 'otherAdditionalFeature',
});

function optionalCategories(selected, values, family) {
  const otherKey = OTHER_KEYS[family];
  return {
    status: selected.length ? 'answered' : 'unanswered',
    selected: selected.map(key => key === otherKey ? 'other' : key),
    ...(selected.includes(otherKey)
      ? { otherLabel: values?.[otherKey]?.attributeName?.trim() }
      : {}),
  };
}

function inconsistencyResponse(state) {
  if (state.inconsistencyStatus === 'none') return { status: 'none', affectedTasks: [] };
  if (state.inconsistencyStatus !== 'present') return { status: 'unanswered', affectedTasks: [] };
  const tasks = [
    ['vowels', state.vowelsInconsistency],
    ['sentences', state.sentencesInconsistency],
    ['extemporaneousSpeech', state.extemporaneousSpeechInconsistency],
  ];
  return {
    status: 'present',
    affectedTasks: tasks
      .filter(([, response]) => response?.isPresent)
      .map(([taskKey, response]) => ({ taskKey, description: response.attributeComment.trim() })),
  };
}

export function validateCapeVrState(state) {
  const errors = {};
  CAPE_VR_STANDARD_KEYS.forEach(key => {
    if (!finiteVas(state[key])) errors.standardVas = 'required';
  });

  const customStarted = nonBlank(state.customVasLabel) || state.customVasValue != null;
  if (customStarted && (!nonBlank(state.customVasLabel) || !finiteVas(state.customVasValue))) {
    errors.customVas = 'incomplete';
  }

  const normalizedCategories = categories(state);
  if (Object.values(normalizedCategories).some(category => !category.value)) errors.categories = 'required';

  const inconsistencies = inconsistencyResponse(state);
  if (!['none', 'present'].includes(inconsistencies.status)) errors.inconsistencies = 'required';
  if (
    inconsistencies.status === 'present' &&
    (!inconsistencies.affectedTasks.length || inconsistencies.affectedTasks.some(task => !nonBlank(task.description)))
  ) errors.inconsistencies = 'incomplete';

  const selectedOtherInstability = state.selectedInstabilities.includes('otherInstability');
  const otherInstabilityLabel = state.instabilitiesValues?.otherInstability?.attributeName;
  if (selectedOtherInstability && !nonBlank(otherInstabilityLabel)) {
    errors.otherInstability = 'required';
  }
  const selectedOtherFeature = state.selectedAdditionalFeatures.includes('otherAdditionalFeature');
  const otherAdditionalFeatureLabel = state.additionalFeaturesValues?.otherAdditionalFeature?.attributeName;
  if (selectedOtherFeature && !nonBlank(otherAdditionalFeatureLabel)) {
    errors.otherAdditionalFeature = 'required';
  }
  if (!readSampleTaskProvenance(state.taskProvenance).valid) errors.taskProvenance = 'required';

  return { valid: Object.keys(errors).length === 0, errors };
}

export function buildCapeVrNormalizedAssessment({ state, assessmentMetadata = {} }) {
  const validation = validateCapeVrState(state);
  if (!validation.valid) {
    const error = new Error('CAPE-Vr response is incomplete.');
    error.code = 'CAPE_VR_RESPONSE_INCOMPLETE';
    error.validation = validation;
    throw error;
  }
  const normalizedTaskProvenance = readSampleTaskProvenance(state.taskProvenance);
  const taskProvenance = {
    taskKeys: normalizedTaskProvenance.taskKeys,
    ...(state.taskProvenance?.sampleClass
      ? { sampleClass: state.taskProvenance.sampleClass }
      : {}),
  };

  const normalizedCategories = categories(state);
  const categoryResponse = key => ({
    status: 'answered',
    value: normalizedCategories[key].value,
    ...(nonBlank(normalizedCategories[key].comment) ? { comment: normalizedCategories[key].comment } : {}),
  });
  const customStarted = nonBlank(state.customVasLabel) || state.customVasValue != null;

  return {
    ...assessmentMetadata,
    responseContract: {
      ...CAPE_VR_CONTRACT,
      completionStatus: 'complete',
      sampleTaskProvenance: taskProvenance,
    },
    instrumentResponse: {
      capeVr: {
        overallSeverity: { status: 'answered', value: state.overallSeverity },
        roughness: { status: 'answered', value: state.roughness },
        breathiness: { status: 'answered', value: state.breathiness },
        strain: { status: 'answered', value: state.strain },
        customVas: customStarted
          ? { status: 'answered', label: state.customVasLabel.trim(), value: state.customVasValue }
          : { status: 'unused' },
        pitch: categoryResponse('pitch'),
        loudness: categoryResponse('loudness'),
        resonance: categoryResponse('resonance'),
        nasality: categoryResponse('nasality'),
        inconsistencies: inconsistencyResponse(state),
        instabilities: optionalCategories(state.selectedInstabilities, state.instabilitiesValues, 'instabilities'),
        additionalFeatures: optionalCategories(state.selectedAdditionalFeatures, state.additionalFeaturesValues, 'additionalFeatures'),
        ...(nonBlank(state.overallImpressionComment)
          ? { overallImpression: { status: 'answered', text: state.overallImpressionComment } }
          : {}),
        taskProvenance,
      },
    },
  };
}
